import type { ConstraintLine } from "building-types/framing/floorFramingConstraintTypes";
import type { FloorEdgeOverridesTable, GeneratedFloorBoundaries } from "./floorAssemblyTypes";
import type {
  BayFramingEdgeOverride,
  BayFramingEdgeOverridesTable,
  FloorBay,
  FloorSplitData,
} from "./floorSplitDataTypes";

/** Prefix for edge IDs created when a split line bisects a floor edge. */
export const FLOOR_BAY_SPLIT_EDGE_PREFIX = "bay-split-";

/**
 * Returns the original edge ID from a bay-split edge ID,
 * or undefined if the given ID is not a bay-split edge.
 */
export function getBaySplitEdgeSourceId(edgeId: string): string | undefined {
  if (!edgeId.startsWith(FLOOR_BAY_SPLIT_EDGE_PREFIX)) {
    return undefined;
  }
  const lastDash = edgeId.lastIndexOf("-");
  if (lastDash <= FLOOR_BAY_SPLIT_EDGE_PREFIX.length) {
    return undefined;
  }
  return edgeId.substring(FLOOR_BAY_SPLIT_EDGE_PREFIX.length, lastDash);
}

/** Returns true if the edge ID was produced by a bay split. */
export function isBaySplitEdge(edgeId: string): boolean {
  return edgeId.startsWith(FLOOR_BAY_SPLIT_EDGE_PREFIX);
}

/** Prefix for edge IDs that lie on a split line segment. */
export const SPLIT_LINE_EDGE_PREFIX = "splitline-";

/** Infix separating the split line ID from the edge index. */
export const SPLIT_LINE_EDGE_INFIX = "-edge-";

/** Returns true if the edge ID identifies a bay perimeter edge derived from a split line. */
export function isSplitLineEdge(edgeId: string): boolean {
  return edgeId.startsWith(SPLIT_LINE_EDGE_PREFIX);
}

/**
 * Parses a split-line edge ID into its split line ID and edge index.
 * Returns undefined if the ID does not match the expected format.
 */
export function parseSplitLineEdgeId(edgeId: string): { splitLineId: string; edgeIndex: number } | undefined {
  if (!edgeId.startsWith(SPLIT_LINE_EDGE_PREFIX)) {
    return undefined;
  }
  const infixPos = edgeId.lastIndexOf(SPLIT_LINE_EDGE_INFIX);
  if (infixPos < SPLIT_LINE_EDGE_PREFIX.length) {
    return undefined;
  }
  const splitLineId = edgeId.substring(SPLIT_LINE_EDGE_PREFIX.length, infixPos);
  const edgeIndexStr = edgeId.substring(infixPos + SPLIT_LINE_EDGE_INFIX.length);
  const edgeIndex = Number(edgeIndexStr);
  if (splitLineId.length === 0 || !Number.isFinite(edgeIndex) || edgeIndex < 0) {
    return undefined;
  }
  return { splitLineId, edgeIndex };
}

/**
 * Strips bay edge overrides keyed to a deleted split line from all bays.
 * Removes entries matching `splitline-{deletedSplitLineId}-edge-*`.
 * When all overrides are removed from a bay, sets `bayEdgeOverrides` to undefined.
 */
export function stripDeletedSplitLineEdgeOverrides(
  splitData: FloorSplitData,
  deletedSplitLineId: string,
): FloorSplitData {
  const prefix = `splitline-${deletedSplitLineId}-edge-`;
  const bays = splitData.bays ?? {};
  let changed = false;
  const updatedBays: Record<string, FloorBay> = {};

  for (const [bayId, bay] of Object.entries(bays)) {
    if (!bay.bayEdgeOverrides) {
      updatedBays[bayId] = bay;
      continue;
    }
    const filtered: Record<string, BayFramingEdgeOverride> = {};
    let kept = 0;
    for (const [edgeId, override] of Object.entries(bay.bayEdgeOverrides)) {
      if (edgeId.startsWith(prefix)) {
        changed = true;
      } else {
        filtered[edgeId] = override;
        kept++;
      }
    }
    updatedBays[bayId] = { ...bay, bayEdgeOverrides: kept > 0 ? filtered : undefined };
  }

  if (!changed) {
    return splitData;
  }
  return { ...splitData, bays: updatedBays };
}

export interface GeneratedBayBoundary {
  readonly bayId: FloorBay["id"];
  readonly boundary: GeneratedFloorBoundaries;
}

export interface GeneratedBayResult {
  readonly bayBoundaries: GeneratedBayBoundary[];
  readonly constraintLines: ConstraintLine[];
}

export interface BayEdgeInheritedProps {
  readonly inherited: FloorEdgeOverridesTable[string];
  readonly overridden: BayFramingEdgeOverridesTable[string];
  readonly effective: { readonly framingConfig?: FloorEdgeOverridesTable[string]["framingConfig"] };
  readonly isSplitLineDerived: boolean;
}
